import { Component, OnInit } from '@angular/core'
import { Observable, forkJoin, from, of } from 'rxjs'
import { catchError, concatMap, mergeMap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { ProductsService } from '../products/products.service'
import { CategoriesService } from '../categories/categories.service'
import { environment } from 'src/environments/environment'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { ZohoService } from 'src/app/integrations/zoho/zoho.service'
import { OdooService } from 'src/app/integrations/odoo/odoo.service'
import { SyncerService } from 'src/app/integrations/syncer.service'
import { HelpersService } from 'src/app/helpers.service'

interface Subcategory {
  categoryImage: string
  subCategoryId: string
  subcategory: string
}

interface Category {
  categoryId: string
  categoryImage: string
  categoryName: string
  subcategories: Subcategory[]
}

@Component({
  selector: 'app-stacks-importer',
  templateUrl: './stacks-importer.component.html',
  styleUrls: ['./stacks-importer.component.scss']
})
export class StacksImporterComponent implements OnInit {
  constructor(
    public builderService: BuilderService,
    private productsService: ProductsService,
    private categoriesService: CategoriesService,
    public odooService: OdooService,
    public vendor: VendorService,
    public zohoService: ZohoService,
    private helpers : HelpersService,
    public Syncer: SyncerService,
  ) {}

  allImagesLength = 0
  imagesUploaded = 0
  parsedCSVData: any
  importingType = 'products'
  skipImages = false

  corruptedImages: any = []

  exportTotalProducts = 0
  exportExistingProducts = 0
  exportMissingProductsCount = 0
  exportCompleted = false

  private readonly BATCH_SIZE = 5

  currentView: 'select-type' | 'select-source' = 'select-type'
  selectedImportType: 'products' | 'categories' | 'orders' = 'products'

  ngOnInit(): void {}

  onTypeSelected(type: string) {
    this.selectedImportType = type as any
    this.currentView = 'select-source'
  }

  onBack() {
    this.currentView = 'select-type'
    this.Syncer.syncProgress = undefined
    this.corruptedImages = []
  }

  onImportCsv(event: { file: File; skipImages: boolean }) {
    this.skipImages = event.skipImages
    const csvType =
      this.selectedImportType === 'categories'
        ? 'category'
        : this.selectedImportType === 'orders'
        ? 'orders'
        : 'products'
    this.importingType = this.selectedImportType
    this.readFileContents(event.file, csvType)
  }

  onImportDirect(source: string) {
    const key = `${source}_${this.selectedImportType}`
    switch (key) {
      case 'woocommerce_products':   this.Syncer.syncAllProductsFromWordPress(); break
      case 'woocommerce_categories': this.Syncer.syncAllCategoriesFromWordPress(); break
      case 'woocommerce_orders':     this.Syncer.syncAllOrdersFromWordPress(); break
      case 'zoho_products':          this.Syncer.syncZohoItems(); break
      case 'odoo_products':          this.Syncer.syncAllProductsFromOdoo(); break
      case 'odoo_categories':        this.Syncer.syncAllCategoriesFromOdoo(); break
    }
  }

  onViewResults() {
    this.corruptedImages = []
  }

  get availableSourceIds(): string[] {
    const ids: string[] = ['csv']
    // if (!this.vendor.isVendor) {
    //   const wpUrl = this.builderService.contentSettings?.wordpress_site_url
    //   if (wpUrl && wpUrl !== '' && !this.zohoService.zohoConnected) {
    ids.push('woocommerce')
    //   }
    //   if (this.zohoService.zohoConnected) {
    ids.push('zoho')
    //   }
    //   if (this.odooService.odooConnected) {
    ids.push('odoo')
    //   }
    // }
    return ids
  }

  // Generate a function that imports csv file and converts it to json

  handleFileInput(event: any, type: string) {
    const file = event.target.files[0]

    if (file) {
      // Check if the file has a CSV extension
      if (file.name.toLowerCase().endsWith('.csv')) {
        this.readFileContents(file, type)
      } else {
        // Display an error message for non-CSV files
        alert('Error: Please choose a CSV file.')
      }
    }
  }

  readFileContents(file: File, type: string) {
    const reader = new FileReader()

    reader.onload = (e: any) => {
      const fileContents = e.target.result
      this.parseGoogleSheetData(fileContents, type)
    }

    reader.readAsText(file)
  }

  handleExportMissingFileInput(event: any) {
    const file = event.target.files[0]

    if (file) {
      if (file.name.toLowerCase().endsWith('.csv')) {
        const reader = new FileReader()
        reader.onload = (e: any) => {
          this.exportMissingProducts(e.target.result, file.name)
        }
        reader.readAsText(file)
      } else {
        alert('Error: Please choose a CSV file.')
      }
    }
  }

  /**
   * Reads a products CSV, checks each productId against the database, and downloads
   * a new CSV containing only the rows whose productId is not yet in the project.
   * @param csvData - Raw CSV text as uploaded.
   * @param fileName - Original file name, used to name the downloaded file.
   *
   * Rows are preserved verbatim (same header, columns, and order) so the exported
   * file can be fed straight back into the normal product import. Rows without a
   * productId cannot be matched and are kept.
   */
  private exportMissingProducts(csvData: string, fileName: string) {
    this.exportCompleted = false

    const cleaned = csvData.replace(/^﻿/, '')
    const lines = this.splitCsvLines(cleaned)

    if (lines.length <= 1) {
      this.exportTotalProducts = 0
      this.exportExistingProducts = 0
      this.exportMissingProductsCount = 0
      this.exportCompleted = true
      return
    }

    const headerLine = lines[0]
    const headers = this.parseCsvLine(headerLine)
    const productIdIndex = headers.indexOf('productId')

    const dataLines = lines.slice(1).filter((line) => line.trim() !== '')

    const uniqueIds = new Set<string>()
    for (const line of dataLines) {
      const fields = this.parseCsvLine(line)
      const productId = productIdIndex >= 0 ? fields[productIdIndex] : ''
      if (productId) {
        uniqueIds.add(productId)
      }
    }

    this.productsService.getExistingProductIds([...uniqueIds]).subscribe((existingIds) => {
      const existingSet = new Set(existingIds)

      const keptLines: string[] = []
      for (const line of dataLines) {
        const fields = this.parseCsvLine(line)
        const productId = productIdIndex >= 0 ? fields[productIdIndex] : ''
        if (productId && existingSet.has(productId)) continue
        keptLines.push(line)
      }

      this.exportTotalProducts = uniqueIds.size
      this.exportExistingProducts = existingSet.size
      this.exportMissingProductsCount = keptLines.length

      if (keptLines.length > 0) {
        const output = [headerLine, ...keptLines].join('\n')
        this.downloadCsv(output, this.buildMissingFileName(fileName))
      }

      this.exportCompleted = true
    })
  }

  private buildMissingFileName(fileName: string): string {
    const base = fileName.toLowerCase().endsWith('.csv') ? fileName.slice(0, -4) : fileName
    return `${base}-missing.csv`
  }

  private downloadCsv(content: string, fileName: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  parseGoogleSheetData(fileContents: string, type: string) {
    this.imagesUploaded = 0
    // Parse the content, convert it to a usable format (e.g., JSON), and then process it.
    if (type === 'products') {
      this.parseProductsCSVData(fileContents).subscribe((parsedCSVData) => {
        const products = parsedCSVData.filter((element: any) => {
          const productId = element.productId?.[0]
          if (!productId) {
            console.warn('[StacksImporterComponent] Skipping product without productId:', element)
          }
          return !!productId
        })

        this.importProducts(products)
      })
      this.importingType = 'products'
    } else if (type === 'category') {
      this.parseCategoriesCSVData(fileContents).subscribe((parsedCSVData) => {
        this.categoriesService.submitCategory(parsedCSVData).subscribe((result: any) => {
          // this.router.navigate(['/products']);
        })
      })
      this.importingType = 'categories'
      // console.log(parsedCategoriesCSVData);
    } else if (type === 'digitalProducts') {
      this.parseDigitalCSVData(fileContents).subscribe((parsedCSVData) => {
        // Map all the parsed CSV Data with the SKU of the product
          this.findProductsBySKU(parsedCSVData, false).subscribe((foundProducts) => {
            if (foundProducts && foundProducts.length > 0) {
              // loop on the found products and submit the codes to the matching product
              for (let index = 0; index < foundProducts.length; index++) {
                const foundProduct = foundProducts[index];
                // console.log('Found product with the same SKU:', foundProduct);
                // Map the SKUs to the codes in the parsed CSV data
                const filteredCodes = parsedCSVData.filter((code: any) => foundProduct.productSKU === code.productSku);
                // Submit the codes to the product
                this.productsService.submitCodeToProduct(foundProduct.Id, filteredCodes, false).subscribe((result: any) => {

                });
              }
            }

            // Handle the variable SKU case

            this.findProductsBySKU(parsedCSVData, true).subscribe((foundProducts) => {
              // console.log('Found products with variable SKU:', foundProducts);
              if (foundProducts && foundProducts.length > 0) {
                // loop on the found products and submit the codes to the matching product
                for (let index = 0; index < foundProducts.length; index++) {
                  const foundProduct = foundProducts[index];
                  // console.log('Found product with the same SKU:', foundProduct);
                  const filteredCodes = parsedCSVData.filter((code: any) =>
                    foundProduct.productVariations?.some(
                      (variation: any) =>
                        variation.sku?.toString() === code.variation_sku?.toString()
                    )
                  );
                  console.log('Filtered Codes:', filteredCodes);
                  // // Submit the codes to the product
                  this.productsService.submitCodeToProduct(foundProduct.Id, filteredCodes, true).subscribe((result: any) => {

                  });
                }
              }
            });
          });




        // this.productsService.submitCodeToProduct(foundProducts[0].Id, filteredCodes).subscribe((result: any) => {

        // });

        // this.categoriesService.submitCategory(parsedCSVData).subscribe((result: any) => {
        //   // this.router.navigate(['/products']);
        // })
      })
      this.importingType = 'digitalProducts'
      // console.log(parsedCategoriesCSVData);
    }
    // Call a function to add the parsed data to Firebase Firestore.
    // this.addToFirestore(parsedData);
  }

  // Parse a single CSV line into fields, respecting quoted values.
  private parseCsvLine(line: string): string[] {
    let result: string[] = []
    let current = ''
    let inQuotes = false

    for (let char of line) {
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim()) // Add the last field
    return result
  }

  // Split raw CSV text into logical lines, respecting newlines inside quoted fields.
  private splitCsvLines(csvData: string): string[] {
    var normalizedData = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    var lines: string[] = []
    var currentLine = ''
    var insideQuotes = false

    // Split into logical lines, respecting quoted newlines
    for (var i = 0; i < normalizedData.length; i++) {
      var char = normalizedData[i]

      if (char === '"') {
        insideQuotes = !insideQuotes
        currentLine += char
      } else if (char === '\n' && !insideQuotes) {
        lines.push(currentLine)
        currentLine = ''
      } else {
        currentLine += char
      }
    }
    if (currentLine) lines.push(currentLine) // Push the last line

    return lines
  }

  // Convert the CSV data to JSON.
  parseProductsCSVData(csvData: string) {
    csvData = csvData.replace(/^\uFEFF/, '')

    const parseCsvWithMultiLine = (csvData: any) => {
      var lines = this.splitCsvLines(csvData)

      // Parse headers and data
      var headers = this.parseCsvLine(lines[0])
      var jsonData = []

      for (var i = 1; i < lines.length; i++) {
        var currentLineFields = this.parseCsvLine(lines[i])
        var obj: any = {}

        for (var j = 0; j < headers.length; j++) {
          obj[headers[j]] = currentLineFields[j] || '' // Handle missing fields
        }

        jsonData.push(obj)
      }

      return jsonData
    }
    var jsonData = parseCsvWithMultiLine(csvData)
    return new Observable<any>((observer) => {
      this.productStructure(jsonData, 'csv').subscribe((transformedAllData) => {
        observer.next(transformedAllData) // Notify the observer that the operation is complete
        observer.complete() // Complete the Observable
      })
    })
  }

  /**
   * Imports products in small concurrent batches to keep memory bounded.
   * At most BATCH_SIZE products are processed at once; each product uploads its
   * own images and is then submitted before the next one is pulled in.
   * @param products - The grouped product objects to import.
   *
   * Progress (Syncer.syncProgress) tracks fully completed products, so it only
   * reaches 100% once every product is actually persisted.
   */
  private importProducts(products: any[]) {
    const total = products.length
    if (total === 0) {
      this.Syncer.syncProgress = 100
      return
    }

    let completed = 0
    this.Syncer.syncProgress = 0

    from(products)
      .pipe(
        mergeMap(
          (product) =>
            this.processOneProduct(product).pipe(
              catchError((error) => {
                console.warn('[StacksImporterComponent] Failed to import product:', error)
                return of(null)
              })
            ),
          this.BATCH_SIZE
        )
      )
      .subscribe({
        next: () => {
          completed++
          this.Syncer.syncProgress = Math.min(99, Math.round((completed / total) * 100))
        },
        error: (error) => {
          console.warn('[StacksImporterComponent] Product import stream error:', error)
        },
        complete: () => {
          this.Syncer.syncProgress = 100
        }
      })
  }

  /**
   * Uploads a single product's images (main image and each variation image),
   * writes the resolved URLs back onto the product, then submits it.
   * @param product - The grouped product object to process.
   * @returns An Observable that completes once the product is persisted.
   *
   * When skipImages is enabled, image uploads are skipped and the product is
   * submitted with its raw image URLs.
   */
  private processOneProduct(product: any): Observable<any> {
    const csvId = product.productId?.[0]

    if (!csvId) {
      return this.resolveProductImages(product).pipe(
        concatMap((resolved) => this.productsService.submitProduct(resolved, ''))
      )
    }

    return this.productsService.getExistingProductIds([csvId]).pipe(
      concatMap((existing) => {
        const updateId = existing.includes(csvId) ? csvId : ''
        return this.resolveProductImages(product).pipe(
          concatMap((resolved) => this.productsService.submitProduct(resolved, updateId))
        )
      })
    )
  }

  /**
   * Resolves the image URLs on a single product by uploading any valid source
   * images and replacing them with the uploaded URLs.
   * @param product - The grouped product object whose images to resolve.
   * @returns An Observable emitting the same product with resolved image URLs.
   */
  private resolveProductImages(product: any): Observable<any> {
    if (this.skipImages) {
      return of(product)
    }

    const uploads: Observable<void>[] = []

    if (this.productsService.isValidImage(product.imageUrl)) {
      uploads.push(this.uploadProductImage(product.imageUrl).pipe(
        concatMap((url) => { product.imageUrl = url; return of(undefined as void) })
      ))
    }

    if (Array.isArray(product.productVariations)) {
      for (const variation of product.productVariations) {
        if (this.productsService.isValidImage(variation.image)) {
          uploads.push(this.uploadProductImage(variation.image).pipe(
            concatMap((url) => { variation.image = url; return of(undefined as void) })
          ))
        }
      }
    }

    if (uploads.length === 0) {
      return of(product)
    }

    return forkJoin(uploads).pipe(concatMap(() => of(product)))
  }

  /**
   * Fetches a source image and uploads it, returning the uploaded URL.
   * @param url - The source image URL to fetch and upload.
   * @returns An Observable emitting the uploaded URL, or the original URL on failure.
   */
  private uploadProductImage(url: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.fetchImageAsFile(url)
        .then((file: any) => {
          this.uploadFile(file).subscribe((uploadedUrl) => {
            observer.next(uploadedUrl)
            observer.complete()
          })
        })
        .catch(() => {
          observer.next(url)
          observer.complete()
        })
    })
  }

  /**
   *
   * @param images Array of images
   * @param jsonData the csv file data
   * @param type The type of image whether image or variation_image "imageUrl"
   * @returns
   */
  uploadImagesToFirebaseStorage(images: any, jsonData: any, type: any) {
    return new Observable<any>((observer) => {
      if (this.skipImages) {
        observer.next(jsonData)
        observer.complete()
        return
      }
      if (!images || images.length == 0) {
        observer.next(jsonData)
        observer.complete()
        return
      }

      const indexes: number[] = []
      for (let i = 0; i < images.length; i++) {
        if (images[i]) {
          indexes.push(i)
        }
      }

      if (indexes.length === 0) {
        observer.next(jsonData)
        observer.complete()
        return
      }

      const chunks: number[][] = []
      for (let i = 0; i < indexes.length; i += this.BATCH_SIZE) {
        chunks.push(indexes.slice(i, i + this.BATCH_SIZE))
      }

      from(chunks)
        .pipe(
          concatMap((chunk) =>
            forkJoin(chunk.map((index) => this.resolveImage(images[index], jsonData, type, index)))
          )
        )
        .subscribe({
          next: () => {},
          error: (error) => observer.error(error),
          complete: () => {
            observer.next(jsonData)
            observer.complete()
          }
        })
    })
  }

  private resolveImage(url: any, jsonData: any, type: any, index: number) {
    const finalize = (resolvedUrl: any) => {
      jsonData[index][type] = resolvedUrl
      this.imagesUploaded++
      if (this.allImagesLength > 1) {
        this.Syncer.syncProgress = Math.round((this.imagesUploaded / (this.allImagesLength - 1)) * 100)
        if (this.Syncer.syncProgress > 100) {
          this.Syncer.syncProgress = 100
        }
      } else {
        this.Syncer.syncProgress = 100
      }
    }

    if (!this.productsService.isValidImage(url)) {
      finalize(url)
      return of(url)
    }

    return new Observable<any>((observer) => {
      this.fetchImageAsFile(url).then((file: any) => {
        this.uploadFile(file).subscribe((imageUploaded) => {
          finalize(imageUploaded)
          observer.next(imageUploaded)
          observer.complete()
        })
      })
    })
  }

  // Convert the CSV data to JSON.
  parseCategoriesCSVData(csvData: string) {
    // Function to parse a CSV line considering quoted fields
    function parseCsvLine(line: any) {
      let result = []
      let current = ''
      let inQuotes = false

      for (let char of line) {
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      result.push(current.trim()) // Add the last field
      return result
    }

    // Normalize line endings and split CSV data
    var normalizedData = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    var lines = normalizedData.split('\n')
    var headers = parseCsvLine(lines[0])
    var categories: any = {}

    var jsonData: any = []

    for (var i = 1; i < lines.length; i++) {
      var currentline = parseCsvLine(lines[i])
      var obj: any = {}

      for (var j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentline[j]
      }

      jsonData.push(obj)
    }

    return new Observable<any>((observer) => {
      this.categoryStructure(jsonData, 'csv').subscribe((transformedAllData) => {
        console.log(transformedAllData)
        observer.next(transformedAllData) // Notify the observer that the operation is complete
        observer.complete() // Complete the Observable
      })
    })
  }

  // Convert the CSV data to JSON.
  parseDigitalCSVData(csvData: string) {
    // Function to parse a CSV line considering quoted fields
    function parseCsvLine(line: any) {
      let result = []
      let current = ''
      let inQuotes = false

      for (let char of line) {
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      result.push(current.trim()) // Add the last field
      return result
    }

    // Normalize line endings and split CSV data
    var normalizedData = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    var lines = normalizedData.split('\n')
    var headers = parseCsvLine(lines[0])

    var jsonData: any = []

    for (var i = 1; i < lines.length; i++) {
      var currentline = parseCsvLine(lines[i])
      var obj: any = {}

      for (var j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentline[j]
      }

      jsonData.push(obj)
    }

    return new Observable<any>((observer) => {
      this.digitalStructure(jsonData, 'csv').subscribe((transformedAllData) => {
        console.log(transformedAllData)
        observer.next(transformedAllData) // Notify the observer that the operation is complete
        observer.complete() // Complete the Observable
      })
    })
  }



  getFilenameFromUrl = (url: string) => {
    // Decode the URL to convert encoded parts to normal characters
    const decodedUrl = decodeURIComponent(url)

    // Split the URL by '/' and get the last part
    const parts = decodedUrl.split('/')

    // Further split the last part by '?' to remove any query parameters
    const lastSegment = parts.pop()?.split('?')[0]

    // Now, split by '-' and take all but the last segment (to remove '1-595x595.jpg')
    const nameParts: any = lastSegment?.split('-')
    nameParts.pop() // Remove the last part ('1-595x595.jpg')

    // Join the parts back together
    return nameParts.join('-')
  }

  fetchImageAsFile = (url: string) => {
    if (!url) {
      return new Promise((resolve, reject) => {
        resolve(null)
      })
    }
    let filename: any = this.getFilenameFromUrl(url)
    if (this.isGoogleDriveUrl(url)) {
      console.log('This is a Google Drive URL.')
      const fileId = url.split('/d/')[1].split('/')[0]
      url = `https://drive.google.com/uc?export=download&id=${fileId}`
      filename = fileId + '.jpg' // Use the file ID as the filename
    } else {
      console.log('This is not a Google Drive URL.') // do nothing
      console.log(url)
    }
    url = environment.proxyUrl+encodeURIComponent(url);
    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => new File([blob], filename, { type: blob.type }))
  }

  isGoogleDriveUrl(url: string) {
    // Regular expression to match Google Drive URL patterns
    const googleDriveRegex = /^(https?:\/\/)?(www\.)?drive\.google\.com\/(file\/d\/|open\?id=)/

    return googleDriveRegex.test(url)
  }

  uploadFile = (file: File) => {
    console.log('start uploading')
    return new Observable<any>((observer) => {
      console.log(file)
      if (!file || file == null) {
        observer.next('')
        observer.complete()
      }
      this.builderService.uploadImage(file, this.builderService.selectedProject, 'importedProducts', file.name ? false : true)?.subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          // array unique the corrupted images
          if (file.name?.trim() && this.corruptedImages.indexOf(file.name) == -1) {
            this.corruptedImages.push(file.name)
          }
          console.log(error, file.name)
          observer.next(error)
          observer.complete()
        }
      )
    })
  }


  // Function to extract attributes
  extractAttributes = (dataRow: any) => {
    if (!dataRow['productName'] || dataRow['productName'] == undefined) {
      return []
    }
    const attributes = Object.keys(dataRow)
      .filter((key) => key.startsWith('attribute_'))
      .map((key) => {
        if (dataRow[key] == undefined || dataRow[key].trim() === '') {
          return null
        }
        return {
          name: key.replace('attribute_', '').trim(),
          value: dataRow[key].trim()
        }
      })
      .filter(attr => attr !== null)

    if (attributes.length <= 0) {
      return [{ name: '', value: '' }]
    }
    return attributes
}

  productStructure = (jsonData: any, type = 'csv') => {
    return new Observable<any>((observer) => {
      const transformedAllData = this.buildProductsMap(jsonData, type)
      observer.next(transformedAllData) // Notify the observer that the operation is complete
      observer.complete() // Complete the Observable
    })
  }

  // Group raw CSV rows into product objects (variations span multiple rows).
  // Images are NOT uploaded here; each product keeps its raw image URLs so they
  // can be uploaded per-product, in small batches, at submit time.
  private buildProductsMap(jsonData: any, type = 'csv') {
    let productsMap: any = {}
    for (var i = 0; i < jsonData.length; i++) {
      if (!jsonData[i].productName || jsonData[i].productName == undefined) {
        continue
      }
      let productId = jsonData[i].productId

      if (!productsMap[productId]) {
        // Initialize the product in the map if it's not already there
        let stock_status = type == 'Woocommerce' ? jsonData[i].stock_status : ''
        if (type == 'Woocommerce') {
          if (stock_status == 'instock' && jsonData[i].stockManagement == false) {
            // unlimited quantity
            jsonData[i].productStock = -1
          } else if (stock_status == 'outofstock') {
            jsonData[i].productStock = 0
          }
        }

        productsMap[productId] = {
          productId: [jsonData[i].productId],
          categoriesIds: this.helpers.normalizeIds(jsonData[i].categoriesIds),
          subCategoriesIds: this.helpers.normalizeIds(jsonData[i].subCategoriesIds),
          date: jsonData[i].date || this.helpers.getCurrentDateString(),
          imageUrl: jsonData[i].imageUrl,
          // maxVarPrice: jsonData[i].maxVarPrice,
          // maxVarSale: jsonData[i].maxVarSale,
          // minVarPrice: jsonData[i].minVarPrice,
          // minVarSale: jsonData[i].minVarSale,
          productAttributes: type == 'Woocommerce' ? jsonData[i].productAttributes : this.extractAttributes(jsonData[i]).length > 0 ? this.extractAttributes(jsonData[i]) : [{ name: '', value: '' }],
          productName: jsonData[i].productName.trim(),
          productSKU: jsonData[i].productSKU,
          productStock: jsonData[i].productStock ? Math.floor(Number(jsonData[i].productStock)) : 0,
          publish: jsonData[i].publish,
          estimatedDeliveryTime: jsonData[i].estimatedDeliveryTime,
          estimatedDeliveryUnit: jsonData[i].estimatedDeliveryUnit,
          userType: jsonData[i].userType || 'customer',
          location: jsonData[i].location || '',
          productFormat: (jsonData[i].productFormat || '').trim() || 'physical',
          codes: [],
          sizeChartUrl: jsonData[i].sizeChartUrl,
          stockManagement: type == 'Woocommerce' ? jsonData[i].stockManagement : false,
          stock_status: type == 'Woocommerce' ? jsonData[i].stock_status : '',
          productVariations: [],
          regularPrice: jsonData[i].regularPrice ? Number(jsonData[i].regularPrice) : '',
          salePrice: jsonData[i].salePrice ? Number(jsonData[i].salePrice) : '',
          productDescription: jsonData[i].productDescription,
          importType: type,
          imageSizeType: jsonData[i].imageSizeType || 'auto',
          productType: 'simple'
        }

      }
      if( jsonData[i].codeID) {
        productsMap[productId].codes.push({
          value: jsonData[i].code,
          codeType: jsonData[i].codeType,
          used: jsonData[i].codeUsed
        })
      }
      // console.log(jsonData[i], jsonData[i].variation_values);
      if (type == 'Woocommerce') {
        productsMap[productId].productVariations = jsonData[i].productVariations
      } else {
        // Add the variation to the product only if it has regularPrice and values
        if (jsonData[i].variation_values || productsMap[productId].productVariations.length === 0) {
          productsMap[productId].productVariations.push({
            image: jsonData[i].image,
            regularPrice: jsonData[i].variation_regularPrice ? Number(jsonData[i].variation_regularPrice) : '',
            salePrice: jsonData[i].variation_salePrice ? Number(jsonData[i].variation_salePrice) : '',
            stockManagement: type === 'Woocommerce' ? jsonData[i].stockManagement : false,
            stock_status: type === 'Woocommerce' ? jsonData[i].stock_status : '',
            sku: jsonData[i].variation_sku,
            stock: jsonData[i].variation_stock ? Math.floor(Number(jsonData[i].variation_stock)) : 0,
            values: jsonData[i].variation_values,
           // Initialize codes as an empty array for each variation
          });
          if (jsonData[i].variation_values) {
            productsMap[productId].productType = 'variable'
          }
        }
      }
    }
    return Object.values(productsMap)
  }

  categoryStructure = (jsonData: any, type = 'csv') => {
    return new Observable<any>((observer) => {
      let catImages: any = []
      let subCatImages: any = []
      for (var i = 0; i < jsonData.length; i++) {
        if (jsonData[i].categoryImage) {
          catImages.push(jsonData[i].categoryImage)
        }
        if (jsonData[i].subCategoryImage) {
          subCatImages.push(jsonData[i].subCategoryImage)
        }
      }
      this.allImagesLength = catImages.length + subCatImages.length
      this.uploadImagesToFirebaseStorage(catImages, jsonData, 'categoryImage').subscribe((responseImages) => {
        jsonData = responseImages
        // upload variation images
        this.uploadImagesToFirebaseStorage(subCatImages, jsonData, 'subCategoryImage').subscribe((responseSubCatImages) => {
          jsonData = responseSubCatImages
          let categories: { [key: string]: Category } = {}

          for (var i = 0; i < jsonData.length; i++) {
            let categoryId = jsonData[i].categoryId

            // Skip if the categoryId is empty or line is invalid
            if (!categoryId || !jsonData[i].categoryName) {
              continue
            }
            // Initialize category if not already present
            if (!categories[categoryId]) {
              categories[categoryId] = {
                categoryId: categoryId,
                categoryImage: jsonData[i].categoryImage,
                categoryName: jsonData[i].categoryName.trim(),
                subcategories: []
              }
            }

            // Add subcategory if present
            if (jsonData[i].subCategoryId) {
              let subCategory: Subcategory = {
                categoryImage: jsonData[i].subCategoryImage,
                subCategoryId: jsonData[i].subCategoryId,
                subcategory: jsonData[i].subCategoryName.trim()
              }

              // Check and add subcategory only if it's not already added
              if (!categories[categoryId].subcategories.some((sub: Subcategory) => sub.subCategoryId === jsonData[i].subCategoryId)) {
                categories[categoryId].subcategories.push(subCategory)
              }
            }
          }
          if (this.allImagesLength == 0) {
            this.Syncer.syncProgress = 100
          }
          // Transform into the desired JSON structure
          let transformedAllData = Object.values(categories)
          observer.next(transformedAllData) // Notify the observer that the operation is complete
          observer.complete() // Complete the Observable
        })
      })
    })
  }

  digitalStructure = (jsonData: any, type = 'csv') => {
    return new Observable<any>((observer) => {
      let codes = [];
      for (var i = 0; i < jsonData.length; i++) {
        let code = jsonData[i].code;
        let variation_code = jsonData[i].variation_code;

        // Skip if the categoryId is empty or line is invalid
        if (!code && !variation_code) {
          continue
        }


        // Initialize category if not already present
        if(!jsonData[i].variation_sku) {
          codes.push({
            productId: jsonData[i].productId,
            productSku: jsonData[i].productSku,
            codeType: jsonData[i].codeType,
            used: jsonData[i].codeUsed,
            value: jsonData[i].code,
            variation_sku: jsonData[i].variation_sku,
          })
        } else {
          // Add variation code if present
          codes.push({
            productId: jsonData[i].productId,
            productSku: jsonData[i].productSku,
            codeType: jsonData[i].variation_codeType,
            used: jsonData[i].variation_codeUsed,
            value: jsonData[i].variation_code,
            variation_sku: jsonData[i].variation_sku,
          })
        }

        // Add subcategory if present
        // if (jsonData[i].subCategoryId) {
        //   let subCategory: Subcategory = {
        //     categoryImage: jsonData[i].subCategoryImage,
        //     subCategoryId: jsonData[i].subCategoryId,
        //     subcategory: jsonData[i].subCategoryName
        //   }

        //   // Check and add subcategory only if it's not already added
        //   if (!categories[categoryId].subcategories.some((sub: Subcategory) => sub.subCategoryId === jsonData[i].subCategoryId)) {
        //     categories[categoryId].subcategories.push(subCategory)
        //   }
        // }
      }
      this.Syncer.syncProgress = 100
      // Transform into the desired JSON structure
      let transformedAllData = Object.values(codes)
      observer.next(transformedAllData) // Notify the observer that the operation is complete
      observer.complete() // Complete the Observable
    })
  }
  /*  Function to find products by SKU
    * @param sku The SKU of the product to find
    * @returns An Observable that emits the found products or an empty array if none are found
    */
  findProductsBySKU = (parsedCSVData: string, variableSKU: boolean) => {
    return new Observable<any>((observer) => {
      if( !variableSKU ) {
        this.productsService.findProductsBySKU(parsedCSVData).subscribe(
          (response) => {
            if (response && response.length > 0) {
              observer.next(response)
              observer.complete() // Complete the Observable
            } else {
              observer.next([]) // Return an empty array if no products found
              observer.complete() // Complete the Observable
            }
          },
          (error) => {
            observer.error(error) // Notify the observer of an error
          }
        )
      } else {
        this.productsService.findProductsByVariableSKU(parsedCSVData).subscribe(
          (response) => {
            if (response && response.length > 0) {
              observer.next(response)
              observer.complete() // Complete the Observable
            } else {
              observer.next([]) // Return an empty array if no products found
              observer.complete() // Complete the Observable
            }
          },
          (error) => {
            observer.error(error) // Notify the observer of an error
          }
        )
      }
    })
  }
}
